import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { subscribeToNotifications, markNotificationAsRead } from '../utils/notifications';
import Transition from '../utils/Transition'; 
import useDropdown from '../hooks/useDropdown';

function DropdownNotifications({ align, isModal, setModalOpen }) {
  const { dropdownOpen, setDropdownOpen, trigger, dropdown } = useDropdown();
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  // Subscribe to notifications
  useEffect(() => {
    if (!auth.currentUser) return;

    const unsubscribe = subscribeToNotifications((newNotifications) => {
      console.log('Received notifications:', newNotifications);
      setNotifications(newNotifications);
    });

    return () => unsubscribe();
  }, []);

  const handleNotificationClick = async (notificationId, postId) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(notifications.filter(notification => notification.id !== notificationId));
      
      // Close modal if we're in modal mode and setModalOpen is a function
      if (isModal && typeof setModalOpen === 'function') {
        setModalOpen(false);
      }

      // Navigate to post
      if (postId) {
        navigate(`/community/forum/post/${postId}`);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'comment':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5z"/>
          </svg>
        );
      case 'message':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M18 2a2 2 0 012 2v10a2 2 0 01-2 2h-3.5L12 19v-3H6a2 2 0 01-2-2V4c0-1.1.9-2 2-2h12z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return isModal ? (
    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
      {notifications.length === 0 ? (
        <li className="py-4 px-4 text-center">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Du har ingen notifikationer i øjeblikket
          </div>
        </li>
      ) : (
        notifications.map((notification) => (
          <li key={notification.id}>
            <Link
              className="block py-3 px-4 hover:bg-gray-50 dark:hover:bg-gray-700/20"
              to={notification.postId ? `/community/forum/post/${notification.postId}` : '#'}
              onClick={() => handleNotificationClick(notification.id, notification.postId)}
            >
              <div className="flex items-center mb-1">
                {getNotificationIcon(notification.type)}
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">{notification.content}</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(notification.createdAt?.toDate()).toLocaleString('da-DK')}
              </span>
            </Link>
          </li>
        ))
      )}
    </ul>
  ) : (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className={`w-8 h-8 flex items-center justify-center hover:bg-gray-100 lg:hover:bg-gray-200 dark:hover:bg-gray-700/50 dark:lg:hover:bg-gray-800 rounded-full ${dropdownOpen ? 'bg-gray-200 dark:bg-gray-800' : ''}`}
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="sr-only">Notifikationer</span>
        <svg
          className="fill-current text-gray-500/80 dark:text-gray-400/80"
          width={16}
          height={16}
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M7 0a7 7 0 0 0-7 7c0 1.202.308 2.33.84 3.316l-.789 2.368a1 1 0 0 0 1.265 1.265l2.595-.865a1 1 0 0 0-.632-1.898l-.698.233.3-.9a1 1 0 0 0-.104-.85A4.97 4.97 0 0 1 2 7a5 5 0 0 1 5-5 4.99 4.99 0 0 1 4.093 2.135 1 1 0 1 0 1.638-1.148A6.99 6.99 0 0 0 7 0Z" />
          <path d="M11 6a5 5 0 0 0 0 10c.807 0 1.567-.194 2.24-.533l1.444.482a1 1 0 0 0 1.265-1.265l-.482-1.444A4.962 4.962 0 0 0 16 11a5 5 0 0 0-5-5Zm-3 5a3 3 0 0 1 6 0c0 .588-.171 1.134-.466 1.6a1 1 0 0 0-.115.82 1 1 0 0 0-.82.114A2.973 2.973 0 0 1 11 14a3 3 0 0 1-3-3Z" />
        </svg>
        {notifications.length > 0 && (
          <div className="absolute top-0 right-0 w-2.5 h-2.5 bg-rose-500 border-2 border-white dark:border-gray-900 rounded-full"></div>
        )}
      </button>
  
      <Transition
        className={`origin-top-right z-10 absolute top-full -mr-48 sm:mr-0 min-w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 py-1.5 rounded-lg shadow-lg overflow-hidden mt-1 ${align === 'right' ? 'right-0' : 'left-0'}`}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase pt-1.5 pb-2 px-4">
            Notifikationer
          </div>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {notifications.length === 0 ? (
              <li className="py-4 px-4 text-center">
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  Du har ingen notifikationer i øjeblikket
                </div>
              </li>
            ) : (
              notifications.map((notification) => (
                <li key={notification.id}>
                  <Link
                    className="block py-3 px-4 hover:bg-gray-50 dark:hover:bg-gray-700/20"
                    to={notification.postId ? `/community/forum/post/${notification.postId}` : '#'}
                    onClick={() => handleNotificationClick(notification.id, notification.postId)}
                  >
                    <div className="flex items-center mb-1">
                      {getNotificationIcon(notification.type)}
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">{notification.content}</span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(notification.createdAt?.toDate()).toLocaleString('da-DK')}
                    </span>
                  </Link>
                </li>
              ))
            )}
          </ul>
        </div>
      </Transition>
    </div>
  );
}

export default DropdownNotifications;